/**
 * Container-width-aware column filter.
 *
 * Drops columns whose ``columnMinWidths[key]`` exceeds the current
 * ``containerWidth``. AntD's own ``responsive`` prop polls
 * ``window.matchMedia`` at viewport scale; with a right pane open the
 * viewport is wide but the table area might be narrow. Measuring the
 * scroll container and filtering columns by what actually fits keeps
 * the table from ever needing a horizontal scrollbar.
 *
 * Returns a memoised filtered column list — same identity across
 * renders unless ``columns`` / ``containerWidth`` / ``columnMinWidths``
 * change.
 */

import { useMemo } from "react";
import type { Key } from "react";

interface ColumnWithKey {
  key?: Key;
}

export function useContainerVisibleColumns<T extends ColumnWithKey>(
  columns: readonly T[],
  containerWidth: number,
  columnMinWidths: Record<string, number>,
): T[] {
  return useMemo(() => {
    return columns.filter((col) => {
      const key = col.key !== undefined ? String(col.key) : "";
      const min = columnMinWidths[key] ?? 0;
      return containerWidth >= min;
    });
  }, [columns, containerWidth, columnMinWidths]);
}
